/** Forked from polished.js https://github.com/styled-components/polished */

import { curry } from 'lodash-es';

// #region Internals
type Styles = {
    [ruleOrSelector: string]: string | number | Styles;
};
type RgbColor = {
    red: number;
    green: number;
    blue: number;
};

type RgbaColor = {
    red: number;
    green: number;
    blue: number;
    alpha: number;
};

const cssRegex = /^([+-]?(?:\d+|\d*\.\d+))([a-z]*|%)$/;
function stripUnit(value: string | number): string | number {
    if (typeof value !== 'string') return value;
    const matchedValue = cssRegex.exec(value);
    return matchedValue ? parseFloat(value) : value;
}
function endsWith(string: string, suffix: string): boolean {
    return string.substr(-suffix.length) === suffix;
}

class PolishedError extends Error {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(code: string | number, ..._args: any[]) {
        super(
            `An error occurred. See https://github.com/styled-components/polished/blob/main/src/internalHelpers/errors.md#${code} for more information.`
        );
    }
}
const pixelsto =
    (to: string) =>
    (pxval: string | number, base: string | number = '16px'): string => {
        let newPxval = pxval;
        let newBase = base;
        if (typeof pxval === 'string') {
            if (!endsWith(pxval, 'px')) {
                throw new PolishedError(69, to, pxval);
            }
            newPxval = stripUnit(pxval);
        }

        if (typeof base === 'string') {
            if (!endsWith(base, 'px')) {
                throw new PolishedError(70, to, base);
            }
            newBase = stripUnit(base);
        }

        if (typeof newPxval === 'string') {
            throw new PolishedError(71, pxval, to);
        }

        if (typeof newBase === 'string') {
            throw new PolishedError(72, base, to);
        }

        return `${newPxval / newBase}${to}`;
    };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getValueAndUnit(value: string | number): any {
    if (typeof value !== 'string') return [value, ''];
    const matchedValue = cssRegex.exec(value);
    if (matchedValue) return [parseFloat(value), matchedValue[2]];
    return [value, undefined];
}

const defaultFontSize = 16;

function convertBase(base: string | number): number {
    const deconstructedValue = getValueAndUnit(base);
    if (deconstructedValue[1] === 'px') {
        return parseFloat(base.toString());
    }

    if (deconstructedValue[1] === '%') {
        return (parseFloat(base.toString()) / 100) * defaultFontSize;
    }

    throw new PolishedError(78, deconstructedValue[1]);
}

function getBaseFromDoc(): number {
    if (typeof document !== 'undefined' && document.documentElement !== null) {
        const rootFontSize = getComputedStyle(document.documentElement).fontSize;
        return rootFontSize ? convertBase(rootFontSize) : defaultFontSize;
    }
    return defaultFontSize;
}

type ConversionFunction = (red: number, green: number, blue: number) => string;

function colorToInt(color: number): number {
    return Math.round(color * 255);
}

function convertToInt(red: number, green: number, blue: number): string {
    return `${colorToInt(red)},${colorToInt(green)},${colorToInt(blue)}`;
}

function hslToRgb(
    hue: number,
    saturation: number,
    lightness: number,
    convert: ConversionFunction = convertToInt
): string {
    if (saturation === 0) {
        // achromatic
        return convert(lightness, lightness, lightness);
    }

    // formulae from https://en.wikipedia.org/wiki/HSL_and_HSV
    const huePrime = (((hue % 360) + 360) % 360) / 60;
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const secondComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));

    let red = 0;
    let green = 0;
    let blue = 0;

    if (huePrime >= 0 && huePrime < 1) {
        red = chroma;
        green = secondComponent;
    } else if (huePrime >= 1 && huePrime < 2) {
        red = secondComponent;
        green = chroma;
    } else if (huePrime >= 2 && huePrime < 3) {
        green = chroma;
        blue = secondComponent;
    } else if (huePrime >= 3 && huePrime < 4) {
        green = secondComponent;
        blue = chroma;
    } else if (huePrime >= 4 && huePrime < 5) {
        red = secondComponent;
        blue = chroma;
    } else if (huePrime >= 5 && huePrime < 6) {
        red = chroma;
        blue = secondComponent;
    }

    const lightnessModification = lightness - chroma / 2;
    const finalRed = red + lightnessModification;
    const finalGreen = green + lightnessModification;
    const finalBlue = blue + lightnessModification;
    return convert(finalRed, finalGreen, finalBlue);
}
const namedColorMap: Record<string, string> = {
    aliceblue: 'f0f8ff',
    antiquewhite: 'faebd7',
    aqua: '00ffff',
    aquamarine: '7fffd4',
    azure: 'f0ffff',
    beige: 'f5f5dc',
    bisque: 'ffe4c4',
    black: '000',
    blanchedalmond: 'ffebcd',
    blue: '0000ff',
    blueviolet: '8a2be2',
    brown: 'a52a2a',
    burlywood: 'deb887',
    cadetblue: '5f9ea0',
    chartreuse: '7fff00',
    chocolate: 'd2691e',
    coral: 'ff7f50',
    cornflowerblue: '6495ed',
    cornsilk: 'fff8dc',
    crimson: 'dc143c',
    cyan: '00ffff',
    darkblue: '00008b',
    darkcyan: '008b8b',
    darkgoldenrod: 'b8860b',
    darkgray: 'a9a9a9',
    darkgreen: '006400',
    darkgrey: 'a9a9a9',
    darkkhaki: 'bdb76b',
    darkmagenta: '8b008b',
    darkolivegreen: '556b2f',
    darkorange: 'ff8c00',
    darkorchid: '9932cc',
    darkred: '8b0000',
    darksalmon: 'e9967a',
    darkseagreen: '8fbc8f',
    darkslateblue: '483d8b',
    darkslategray: '2f4f4f',
    darkslategrey: '2f4f4f',
    darkturquoise: '00ced1',
    darkviolet: '9400d3',
    deeppink: 'ff1493',
    deepskyblue: '00bfff',
    dimgray: '696969',
    dimgrey: '696969',
    dodgerblue: '1e90ff',
    firebrick: 'b22222',
    floralwhite: 'fffaf0',
    forestgreen: '228b22',
    fuchsia: 'ff00ff',
    gainsboro: 'dcdcdc',
    ghostwhite: 'f8f8ff',
    gold: 'ffd700',
    goldenrod: 'daa520',
    gray: '808080',
    green: '008000',
    greenyellow: 'adff2f',
    grey: '808080',
    honeydew: 'f0fff0',
    hotpink: 'ff69b4',
    indianred: 'cd5c5c',
    indigo: '4b0082',
    ivory: 'fffff0',
    khaki: 'f0e68c',
    lavender: 'e6e6fa',
    lavenderblush: 'fff0f5',
    lawngreen: '7cfc00',
    lemonchiffon: 'fffacd',
    lightblue: 'add8e6',
    lightcoral: 'f08080',
    lightcyan: 'e0ffff',
    lightgoldenrodyellow: 'fafad2',
    lightgray: 'd3d3d3',
    lightgreen: '90ee90',
    lightgrey: 'd3d3d3',
    lightpink: 'ffb6c1',
    lightsalmon: 'ffa07a',
    lightseagreen: '20b2aa',
    lightskyblue: '87cefa',
    lightslategray: '789',
    lightslategrey: '789',
    lightsteelblue: 'b0c4de',
    lightyellow: 'ffffe0',
    lime: '0f0',
    limegreen: '32cd32',
    linen: 'faf0e6',
    magenta: 'f0f',
    maroon: '800000',
    mediumaquamarine: '66cdaa',
    mediumblue: '0000cd',
    mediumorchid: 'ba55d3',
    mediumpurple: '9370db',
    mediumseagreen: '3cb371',
    mediumslateblue: '7b68ee',
    mediumspringgreen: '00fa9a',
    mediumturquoise: '48d1cc',
    mediumvioletred: 'c71585',
    midnightblue: '191970',
    mintcream: 'f5fffa',
    mistyrose: 'ffe4e1',
    moccasin: 'ffe4b5',
    navajowhite: 'ffdead',
    navy: '000080',
    oldlace: 'fdf5e6',
    olive: '808000',
    olivedrab: '6b8e23',
    orange: 'ffa500',
    orangered: 'ff4500',
    orchid: 'da70d6',
    palegoldenrod: 'eee8aa',
    palegreen: '98fb98',
    paleturquoise: 'afeeee',
    palevioletred: 'db7093',
    papayawhip: 'ffefd5',
    peachpuff: 'ffdab9',
    peru: 'cd853f',
    pink: 'ffc0cb',
    plum: 'dda0dd',
    powderblue: 'b0e0e6',
    purple: '800080',
    rebeccapurple: '639',
    red: 'f00',
    rosybrown: 'bc8f8f',
    royalblue: '4169e1',
    saddlebrown: '8b4513',
    salmon: 'fa8072',
    sandybrown: 'f4a460',
    seagreen: '2e8b57',
    seashell: 'fff5ee',
    sienna: 'a0522d',
    silver: 'c0c0c0',
    skyblue: '87ceeb',
    slateblue: '6a5acd',
    slategray: '708090',
    slategrey: '708090',
    snow: 'fffafa',
    springgreen: '00ff7f',
    steelblue: '4682b4',
    tan: 'd2b48c',
    teal: '008080',
    thistle: 'd8bfd8',
    tomato: 'ff6347',
    turquoise: '40e0d0',
    violet: 'ee82ee',
    wheat: 'f5deb3',
    white: 'fff',
    whitesmoke: 'f5f5f5',
    yellow: 'ff0',
    yellowgreen: '9acd32'
};

function nameToHex(color: string): string {
    if (typeof color !== 'string') return color;
    const normalizedColorName = color.toLowerCase();
    return namedColorMap[normalizedColorName] ? `#${namedColorMap[normalizedColorName]}` : color;
}

const reduceHexValue = (value: string): string => {
    if (value.length === 7 && value[1] === value[2] && value[3] === value[4] && value[5] === value[6]) {
        return `#${value[1]}${value[3]}${value[5]}`;
    }
    return value;
};
function toHex(value: number): string {
    const hex = value.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
}
function guard(lowerBoundary: number, upperBoundary: number, value: number): number {
    return Math.max(lowerBoundary, Math.min(upperBoundary, value));
}

// #endregion

export const rem: (value: string | number, base?: string | number) => string = pixelsto('rem');
export function hideVisually(): Styles {
    return {
        border: '0',
        clip: 'rect(0 0 0 0)',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        padding: '0',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: '1px'
    };
}
export function remToPx(value: string | number, base?: string | number): string {
    const deconstructedValue = getValueAndUnit(value);

    if (deconstructedValue[1] !== 'rem' && deconstructedValue[1] !== '') {
        throw new PolishedError(77, deconstructedValue[1]);
    }

    const newBase = base ? convertBase(base) : getBaseFromDoc();
    return `${deconstructedValue[0] * newBase}px`;
}

export const transparentize: (amount: number | string, color: string) => string = curry(function (
    amount: number | string,
    color: string
): string {
    if (color === 'transparent') return color;
    const parsedColor = parseToRgb(color);
    const alpha: number = 'alpha' in parsedColor && typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;
    const colorWithAlpha = {
        ...parsedColor,
        alpha: guard(0, 1, +(alpha * 100 - parseFloat(amount.toString()) * 100).toFixed(2) / 100)
    };
    return rgba(colorWithAlpha);
});

const mix: (weight: number | string, color: string, otherColor: string) => string = curry(function (
    weight: number | string,
    color: string,
    otherColor: string
): string {
    if (color === 'transparent') return otherColor;
    if (otherColor === 'transparent') return color;
    if (weight === 0) return otherColor;
    const parsedColor1 = parseToRgb(color);
    const color1 = {
        ...parsedColor1,
        alpha: 'alpha' in parsedColor1 && typeof parsedColor1.alpha === 'number' ? parsedColor1.alpha : 1
    };

    const parsedColor2 = parseToRgb(otherColor);
    const color2 = {
        ...parsedColor2,
        alpha: 'alpha' in parsedColor2 && typeof parsedColor2.alpha === 'number' ? parsedColor2.alpha : 1
    };

    // The formula is copied from the original Sass implementation:
    // http://sass-lang.com/documentation/Sass/Script/Functions.html#mix-instance_method
    const alphaDelta = color1.alpha - color2.alpha;
    const x = parseFloat(weight.toString()) * 2 - 1;
    const y = x * alphaDelta === -1 ? x : x + alphaDelta;
    const z = 1 + x * alphaDelta;
    const weight1 = (y / z + 1) / 2.0;
    const weight2 = 1 - weight1;

    const mixedColor = {
        red: Math.floor(color1.red * weight1 + color2.red * weight2),
        green: Math.floor(color1.green * weight1 + color2.green * weight2),
        blue: Math.floor(color1.blue * weight1 + color2.blue * weight2),
        alpha: color1.alpha * parseFloat(weight.toString()) + color2.alpha * (1 - parseFloat(weight.toString()))
    };

    return rgba(mixedColor);
});

export const shade: (percentage: number | string, color: string) => string = curry(function (
    percentage: number | string,
    color: string
): string {
    if (color === 'transparent') return color;
    return mix(parseFloat(percentage.toString()), 'rgb(0, 0, 0)', color);
});

const hexRegex = /^#[a-fA-F0-9]{6}$/;
const hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
const reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
const reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
const rgbRegex = /^rgb\(\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*\)$/i;
const rgbaRegex =
    /^rgb(?:a)?\(\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,|\/)\s*([-+]?\d*[.]?\d+[%]?)\s*\)$/i;
const hslRegex =
    /^hsl\(\s*(\d{0,3}[.]?[0-9]+(?:deg)?)\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
const hslaRegex =
    /^hsl(?:a)?\(\s*(\d{0,3}[.]?[0-9]+(?:deg)?)\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,|\/)\s*([-+]?\d*[.]?\d+[%]?)\s*\)$/i;

export function rgba(
    firstValue: RgbaColor | number | string,
    secondValue?: number,
    thirdValue?: number,
    fourthValue?: number
): string {
    if (typeof firstValue === 'string' && typeof secondValue === 'number') {
        const rgbValue = parseToRgb(firstValue);
        return `rgba(${rgbValue.red},${rgbValue.green},${rgbValue.blue},${secondValue})`;
    } else if (
        typeof firstValue === 'number' &&
        typeof secondValue === 'number' &&
        typeof thirdValue === 'number' &&
        typeof fourthValue === 'number'
    ) {
        return fourthValue >= 1
            ? rgb(firstValue, secondValue, thirdValue)
            : `rgba(${firstValue},${secondValue},${thirdValue},${fourthValue})`;
    } else if (
        typeof firstValue === 'object' &&
        secondValue === undefined &&
        thirdValue === undefined &&
        fourthValue === undefined
    ) {
        return firstValue.alpha >= 1
            ? rgb(firstValue.red, firstValue.green, firstValue.blue)
            : `rgba(${firstValue.red},${firstValue.green},${firstValue.blue},${firstValue.alpha})`;
    }

    throw new PolishedError(7);
}

export function rgb(value: RgbColor | number, green?: number, blue?: number): string {
    if (typeof value === 'number' && typeof green === 'number' && typeof blue === 'number') {
        return reduceHexValue(`#${toHex(value)}${toHex(green)}${toHex(blue)}`);
    } else if (typeof value === 'object' && green === undefined && blue === undefined) {
        return reduceHexValue(`#${toHex(value.red)}${toHex(value.green)}${toHex(value.blue)}`);
    }

    throw new PolishedError(6);
}

export function parseToRgb(color: string): RgbColor | RgbaColor {
    if (typeof color !== 'string') {
        throw new PolishedError(3);
    }
    const normalizedColor = nameToHex(color);
    if (hexRegex.exec(normalizedColor)) {
        return {
            red: parseInt(`${normalizedColor[1]}${normalizedColor[2]}`, 16),
            green: parseInt(`${normalizedColor[3]}${normalizedColor[4]}`, 16),
            blue: parseInt(`${normalizedColor[5]}${normalizedColor[6]}`, 16)
        };
    }
    if (hexRgbaRegex.exec(normalizedColor)) {
        const alpha = parseFloat((parseInt(`${normalizedColor[7]}${normalizedColor[8]}`, 16) / 255).toFixed(2));
        return {
            red: parseInt(`${normalizedColor[1]}${normalizedColor[2]}`, 16),
            green: parseInt(`${normalizedColor[3]}${normalizedColor[4]}`, 16),
            blue: parseInt(`${normalizedColor[5]}${normalizedColor[6]}`, 16),
            alpha
        };
    }
    if (reducedHexRegex.exec(normalizedColor)) {
        return {
            red: parseInt(`${normalizedColor[1]}${normalizedColor[1]}`, 16),
            green: parseInt(`${normalizedColor[2]}${normalizedColor[2]}`, 16),
            blue: parseInt(`${normalizedColor[3]}${normalizedColor[3]}`, 16)
        };
    }
    if (reducedRgbaHexRegex.exec(normalizedColor)) {
        const alpha = parseFloat((parseInt(`${normalizedColor[4]}${normalizedColor[4]}`, 16) / 255).toFixed(2));
        return {
            red: parseInt(`${normalizedColor[1]}${normalizedColor[1]}`, 16),
            green: parseInt(`${normalizedColor[2]}${normalizedColor[2]}`, 16),
            blue: parseInt(`${normalizedColor[3]}${normalizedColor[3]}`, 16),
            alpha
        };
    }
    const rgbMatched = rgbRegex.exec(normalizedColor);
    if (rgbMatched) {
        return {
            red: parseInt(`${rgbMatched[1]}`, 10),
            green: parseInt(`${rgbMatched[2]}`, 10),
            blue: parseInt(`${rgbMatched[3]}`, 10)
        };
    }
    const rgbaMatched = rgbaRegex.exec(normalizedColor.substring(0, 50));
    if (rgbaMatched) {
        return {
            red: parseInt(`${rgbaMatched[1]}`, 10),
            green: parseInt(`${rgbaMatched[2]}`, 10),
            blue: parseInt(`${rgbaMatched[3]}`, 10),
            alpha:
                parseFloat(`${rgbaMatched[4]}`) > 1
                    ? parseFloat(`${rgbaMatched[4]}`) / 100
                    : parseFloat(`${rgbaMatched[4]}`)
        };
    }
    const hslMatched = hslRegex.exec(normalizedColor);
    if (hslMatched) {
        const hue = parseInt(`${hslMatched[1]}`, 10);
        const saturation = parseInt(`${hslMatched[2]}`, 10) / 100;
        const lightness = parseInt(`${hslMatched[3]}`, 10) / 100;
        const rgbColorString = `rgb(${hslToRgb(hue, saturation, lightness)})`;
        const hslRgbMatched = rgbRegex.exec(rgbColorString);
        if (!hslRgbMatched) {
            throw new PolishedError(4, normalizedColor, rgbColorString);
        }
        return {
            red: parseInt(`${hslRgbMatched[1]}`, 10),
            green: parseInt(`${hslRgbMatched[2]}`, 10),
            blue: parseInt(`${hslRgbMatched[3]}`, 10)
        };
    }
    const hslaMatched = hslaRegex.exec(normalizedColor.substring(0, 50));
    if (hslaMatched) {
        const hue = parseInt(`${hslaMatched[1]}`, 10);
        const saturation = parseInt(`${hslaMatched[2]}`, 10) / 100;
        const lightness = parseInt(`${hslaMatched[3]}`, 10) / 100;
        const rgbColorString = `rgb(${hslToRgb(hue, saturation, lightness)})`;
        const hslRgbMatched = rgbRegex.exec(rgbColorString);
        if (!hslRgbMatched) {
            throw new PolishedError(4, normalizedColor, rgbColorString);
        }
        return {
            red: parseInt(`${hslRgbMatched[1]}`, 10),
            green: parseInt(`${hslRgbMatched[2]}`, 10),
            blue: parseInt(`${hslRgbMatched[3]}`, 10),
            alpha:
                parseFloat(`${hslaMatched[4]}`) > 1
                    ? parseFloat(`${hslaMatched[4]}`) / 100
                    : parseFloat(`${hslaMatched[4]}`)
        };
    }
    throw new PolishedError(5);
}
